/**
 * RootView — underground cross-section of a bed.
 *
 * When the user toggles "Underground view" on the Bed Designer, we replace
 * the standard top-down grid with a side-on slice through the soil so they
 * can see what's happening below the surface: which crops have deep
 * taproots (carrots, parsnips), shallow fibrous roots (lettuce, herbs),
 * tubers (potatoes), or bulbs (onions). Educational + visually distinctive.
 *
 * The drawing is pure SVG — no external assets, no API calls. Each crop is
 * placed at its anchor column proportional to the bed's layout, then
 * rendered as a small "above-soil" plant + species-specific root system
 * below the soil line. We reuse `visualKeyForCrop()` from VeggieIcon so
 * crops without a dedicated root profile fall back to a generic shape.
 */
import { Sprout } from "lucide-react";
import { visualKeyForCrop } from "@/components/VeggieIcon";

// ─────────────────────────────────────────────────────────────────────────
// Per-visual-key root profiles. Coordinates are relative to a single
// "tile" of width 60 and height 110 (45 above-soil + 65 below). The
// soil line sits at y=45.
// ─────────────────────────────────────────────────────────────────────────

const SOIL_LINE_Y = 45;
const TILE_W = 60;
const TILE_H = 110;

const TOKENS = {
    leaf: "#6B8C5C",
    leafDark: "#4A6B3A",
    stem: "#5A7A3C",
    soil: "#8B6F47",
    soilDark: "#5A4732",
    soilLight: "#B0916C",
    root: "#D9A86C",
    rootDark: "#9C6B30",
    fruitRed: "#C06547",
    fruitGreen: "#9BA84E",
    fruitOrange: "#D07C3E",
    bulb: "#E8D9A5",
    tuber: "#A37242",
};

function AboveSoilStem({ height = 18, color = TOKENS.stem }) {
    return <line x1={TILE_W / 2} y1={SOIL_LINE_Y} x2={TILE_W / 2} y2={SOIL_LINE_Y - height} stroke={color} strokeWidth="1.4" strokeLinecap="round" />;
}

function Leaves({ height = 22, spread = 14 }) {
    const cx = TILE_W / 2;
    const ty = SOIL_LINE_Y - height;
    return (
        <g>
            <ellipse cx={cx - spread} cy={ty + 4} rx={spread / 1.4} ry="4" fill={TOKENS.leaf} />
            <ellipse cx={cx + spread} cy={ty + 4} rx={spread / 1.4} ry="4" fill={TOKENS.leafDark} />
            <ellipse cx={cx} cy={ty - 2} rx={spread / 1.6} ry="5" fill={TOKENS.leaf} />
        </g>
    );
}

// ─────────────────────────────────────────────────────────────────────────
// Root profiles per visual-key — each returns SVG nodes anchored to the
// tile's center column at y = SOIL_LINE_Y.
// ─────────────────────────────────────────────────────────────────────────
function TaprootDeep() {
    const cx = TILE_W / 2;
    return (
        <g>
            {/* Long orange tapered taproot — carrots, parsnips */}
            <path
                d={`M ${cx - 6} ${SOIL_LINE_Y + 1} Q ${cx - 4} ${SOIL_LINE_Y + 35} ${cx} ${SOIL_LINE_Y + 55} Q ${cx + 4} ${SOIL_LINE_Y + 35} ${cx + 6} ${SOIL_LINE_Y + 1} Z`}
                fill={TOKENS.fruitOrange}
                stroke={TOKENS.rootDark}
                strokeWidth="0.5"
            />
            {/* Side hairs */}
            {[18, 30, 42].map((dy) => (
                <g key={`hair-${dy}`}>
                    <path d={`M ${cx - 4} ${SOIL_LINE_Y + dy} L ${cx - 12} ${SOIL_LINE_Y + dy + 2}`} stroke={TOKENS.rootDark} strokeWidth="0.7" fill="none" />
                    <path d={`M ${cx + 4} ${SOIL_LINE_Y + dy} L ${cx + 12} ${SOIL_LINE_Y + dy + 2}`} stroke={TOKENS.rootDark} strokeWidth="0.7" fill="none" />
                </g>
            ))}
        </g>
    );
}

function RoundRoot() {
    const cx = TILE_W / 2;
    const cy = SOIL_LINE_Y + 14;
    return (
        <g>
            <ellipse cx={cx} cy={cy} rx="11" ry="9" fill={TOKENS.fruitRed} stroke={TOKENS.rootDark} strokeWidth="0.5" />
            {/* tail root */}
            <path d={`M ${cx} ${cy + 8} Q ${cx - 1} ${cy + 25} ${cx + 1} ${cy + 35}`} stroke={TOKENS.rootDark} strokeWidth="1" fill="none" strokeLinecap="round" />
        </g>
    );
}

function FibrousShallow() {
    const cx = TILE_W / 2;
    return (
        <g>
            {/* short, branching fibrous roots — lettuce, brassicas, leafy greens */}
            {[-8, -4, 0, 4, 8].map((dx) => (
                <path
                    key={`fib-${dx}`}
                    d={`M ${cx + dx / 2} ${SOIL_LINE_Y + 1}
                        Q ${cx + dx * 1.5} ${SOIL_LINE_Y + 8} ${cx + dx * 1.7} ${SOIL_LINE_Y + 18}`}
                    stroke={TOKENS.rootDark}
                    strokeWidth="0.9"
                    fill="none"
                    strokeLinecap="round"
                />
            ))}
        </g>
    );
}

function Bulb() {
    const cx = TILE_W / 2;
    const cy = SOIL_LINE_Y + 9;
    return (
        <g>
            <path
                d={`M ${cx - 8} ${cy} Q ${cx - 8} ${cy - 5} ${cx} ${cy - 5} Q ${cx + 8} ${cy - 5} ${cx + 8} ${cy} Q ${cx + 8} ${cy + 8} ${cx} ${cy + 8} Q ${cx - 8} ${cy + 8} ${cx - 8} ${cy} Z`}
                fill={TOKENS.bulb}
                stroke={TOKENS.rootDark}
                strokeWidth="0.5"
            />
            {/* fine basal roots */}
            {[-4, 0, 4].map((dx) => (
                <path
                    key={`bulb-root-${dx}`}
                    d={`M ${cx + dx} ${cy + 8} L ${cx + dx} ${cy + 22}`}
                    stroke={TOKENS.rootDark}
                    strokeWidth="0.7"
                    fill="none"
                    strokeLinecap="round"
                />
            ))}
        </g>
    );
}

function Tubers() {
    const cx = TILE_W / 2;
    return (
        <g>
            {/* potato — bushy roots + 3 round tubers nestled in the soil */}
            {[-8, 0, 8].map((dx, i) => (
                <ellipse
                    key={`tuber-${dx}`}
                    cx={cx + dx}
                    cy={SOIL_LINE_Y + 16 + (i % 2) * 4}
                    rx="6"
                    ry="4.5"
                    fill={TOKENS.tuber}
                    stroke={TOKENS.rootDark}
                    strokeWidth="0.4"
                />
            ))}
            {[-12, -4, 4, 12].map((dx) => (
                <path
                    key={`tuber-root-${dx}`}
                    d={`M ${cx + dx / 2} ${SOIL_LINE_Y + 1}
                        Q ${cx + dx} ${SOIL_LINE_Y + 6} ${cx + dx} ${SOIL_LINE_Y + 12}`}
                    stroke={TOKENS.rootDark}
                    strokeWidth="0.7"
                    fill="none"
                />
            ))}
        </g>
    );
}

function VineRoot() {
    const cx = TILE_W / 2;
    return (
        <g>
            {/* squash, cucumber — main central root + lateral spread */}
            <path d={`M ${cx} ${SOIL_LINE_Y} L ${cx} ${SOIL_LINE_Y + 30}`} stroke={TOKENS.rootDark} strokeWidth="1.2" />
            {[12, 22].map((dy) => (
                <g key={`vine-${dy}`}>
                    <path d={`M ${cx} ${SOIL_LINE_Y + dy} Q ${cx - 12} ${SOIL_LINE_Y + dy + 5} ${cx - 18} ${SOIL_LINE_Y + dy + 12}`} stroke={TOKENS.rootDark} strokeWidth="0.7" fill="none" />
                    <path d={`M ${cx} ${SOIL_LINE_Y + dy} Q ${cx + 12} ${SOIL_LINE_Y + dy + 5} ${cx + 18} ${SOIL_LINE_Y + dy + 12}`} stroke={TOKENS.rootDark} strokeWidth="0.7" fill="none" />
                </g>
            ))}
        </g>
    );
}

function Berry() {
    const cx = TILE_W / 2;
    return (
        <g>
            {/* short rosette of fibrous roots */}
            {[-6, -3, 0, 3, 6].map((dx) => (
                <path
                    key={`berry-${dx}`}
                    d={`M ${cx + dx / 2} ${SOIL_LINE_Y + 1}
                        Q ${cx + dx * 1.4} ${SOIL_LINE_Y + 6} ${cx + dx * 1.8} ${SOIL_LINE_Y + 14}`}
                    stroke={TOKENS.rootDark}
                    strokeWidth="0.8"
                    fill="none"
                    strokeLinecap="round"
                />
            ))}
        </g>
    );
}

const ROOT_PROFILES = {
    "fruit-round": { above: () => (<><AboveSoilStem height={28} /><Leaves height={32} spread={16} /></>), root: VineRoot },
    "fruit-long":  { above: () => (<><AboveSoilStem height={28} /><Leaves height={32} spread={16} /></>), root: VineRoot },
    "vine-fruit":  { above: () => (<><AboveSoilStem height={20} /><Leaves height={24} spread={20} /></>), root: VineRoot },
    "root-long":   { above: () => (<Leaves height={20} spread={10} />), root: TaprootDeep },
    "root-round":  { above: () => (<Leaves height={18} spread={12} />), root: RoundRoot },
    "leafy-head":  { above: () => (<Leaves height={22} spread={16} />), root: FibrousShallow },
    "leafy-loose": { above: () => (<Leaves height={24} spread={18} />), root: FibrousShallow },
    "branching":   { above: () => (<><AboveSoilStem height={26} color={TOKENS.stem} /><Leaves height={30} spread={14} /></>), root: FibrousShallow },
    "bulb":        { above: () => (<Leaves height={26} spread={6} />), root: Bulb },
    "stalk":       { above: () => (<Leaves height={32} spread={5} />), root: Bulb },
    "legume":      { above: () => (<><AboveSoilStem height={30} /><Leaves height={34} spread={12} /></>), root: FibrousShallow },
    "potato":      { above: () => (<Leaves height={20} spread={14} />), root: Tubers },
    "herb-small":  { above: () => (<Leaves height={16} spread={10} />), root: FibrousShallow },
    "herb-woody":  { above: () => (<><AboveSoilStem height={18} color={TOKENS.soilDark} /><Leaves height={22} spread={10} /></>), root: FibrousShallow },
    "flower":      { above: () => (<><AboveSoilStem height={26} /><circle cx={TILE_W / 2} cy={SOIL_LINE_Y - 28} r="4" fill={TOKENS.fruitOrange} /></>), root: FibrousShallow },
    "berry":       { above: () => (<Leaves height={14} spread={12} />), root: Berry },
    "rhubarb":     { above: () => (<Leaves height={28} spread={20} />), root: RoundRoot },
    "generic":     { above: () => (<><AboveSoilStem height={20} /><Leaves height={24} spread={12} /></>), root: FibrousShallow },
};

// ─────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────
function CropTile({ crop }) {
    const key = visualKeyForCrop(crop?.crop_name);
    const profile = ROOT_PROFILES[key] || ROOT_PROFILES.generic;
    return (
        <svg
            width="100%"
            viewBox={`0 0 ${TILE_W} ${TILE_H}`}
            preserveAspectRatio="xMidYMid meet"
            data-testid={`root-tile-${crop?.id || "empty"}`}
            data-visual-key={key}
        >
            {/* Above-soil sky tint */}
            <rect x="0" y="0" width={TILE_W} height={SOIL_LINE_Y} fill="rgba(247, 243, 230, 0.6)" />
            {/* Below-soil dirt */}
            <defs>
                <linearGradient id={`soil-${crop?.id || "x"}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={TOKENS.soilLight} />
                    <stop offset="100%" stopColor={TOKENS.soilDark} />
                </linearGradient>
            </defs>
            <rect x="0" y={SOIL_LINE_Y} width={TILE_W} height={TILE_H - SOIL_LINE_Y} fill={`url(#soil-${crop?.id || "x"})`} />
            {/* Soil-line */}
            <line x1="0" y1={SOIL_LINE_Y} x2={TILE_W} y2={SOIL_LINE_Y} stroke={TOKENS.soilDark} strokeWidth="0.6" opacity="0.6" />
            {/* Crop above + below */}
            {crop && (
                <>
                    {profile.above()}
                    {profile.root()}
                </>
            )}
        </svg>
    );
}

export function RootView({ occupancy }) {
    if (!occupancy) return null;
    const { grid_cols, cells, crops } = occupancy;

    // Flatten cells into one row per grid row (above + below soil shown
    // per-cell; the soil line is shared across the whole strip).
    return (
        <div
            className="rounded-lg border border-[var(--vm-border)] bg-[var(--vm-input)]/30 p-4 overflow-x-auto"
            data-testid="root-view"
            aria-label="Underground cross-section of the bed"
        >
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--vm-sage)] mb-2 flex items-center gap-2">
                <Sprout className="w-3 h-3" /> Underground view · soil cross-section
            </div>
            <div className="space-y-2">
                {cells.map((row, rIdx) => (
                    <div
                        key={`row-${rIdx}`}
                        className="grid gap-1"
                        style={{ gridTemplateColumns: `repeat(${grid_cols}, minmax(50px, 1fr))` }}
                        data-testid={`root-row-${rIdx + 1}`}
                    >
                        {row.map((ids, cIdx) => {
                            const list = Array.isArray(ids) ? ids : (ids ? [ids] : []);
                            const primary = list[0] ? crops?.[list[0]] : null;
                            return (
                                <div key={`cell-${rIdx}-${cIdx}`} className="rounded-md overflow-hidden bg-[var(--vm-bg)] border border-[var(--vm-border)]/60 aspect-[6/11]">
                                    <CropTile crop={primary} />
                                    {primary && (
                                        <div className="px-1 py-0.5 text-[9px] text-center truncate text-[var(--vm-text-secondary)] bg-[var(--vm-card)] border-t border-[var(--vm-border)]">
                                            {primary.crop_name}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
            <p className="text-xs text-[var(--vm-text-muted)] italic mt-3">
                Schematic, not to scale — tap toggle again to return to the top-down grid.
            </p>
        </div>
    );
}
